import type { ReactNode } from 'react';
import { cn } from './cn.js';

export interface TabDef<K extends string> {
  key: K;
  label: ReactNode;
  count?: number;
}

/** Onglets soulignés, navigables au clavier (flèches). */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: TabDef<K>[];
  value: K;
  onChange: (k: K) => void;
  label: string;
}) {
  const move = (dir: number) => {
    const i = tabs.findIndex((t) => t.key === value);
    const next = tabs[(i + dir + tabs.length) % tabs.length];
    if (next) onChange(next.key);
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex flex-wrap gap-x-1 shadow-[inset_0_-1px_0_rgb(var(--c-line-soft))]"
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') move(1);
        if (e.key === 'ArrowLeft') move(-1);
      }}
    >
      {tabs.map((t) => {
        const on = t.key === value;
        return (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={cn(
              'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-2.5 pb-3 pt-2 font-display text-[14px] font-semibold lg:px-3.5 lg:text-[15px]',
              'transition-colors duration-base ease-od',
              on ? 'border-navy-600 text-ink' : 'border-transparent text-ink-3 hover:text-ink',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span
                className={cn(
                  'tnum rounded-pill px-1.5 text-[12px] font-semibold leading-5',
                  on ? 'bg-navy-50 text-navy-600' : 'bg-surface-2 text-ink-3',
                )}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
